import type { StakeholderType } from "./stakeholderConstants";
import {
  STAKEHOLDER_AFFECTED_PUBLIC,
  STAKEHOLDER_COMMANDER,
  STAKEHOLDER_EMERGENCY_RESPONDER,
  STAKEHOLDER_EXCAVATOR,
  STAKEHOLDER_FARMER,
  STAKEHOLDER_OTHER,
  STAKEHOLDER_PUBLIC_OFFICIAL,
  STAKEHOLDER_SCHOOL_REP,
  STAKEHOLDER_TIER_II,
} from "./stakeholderConstants";

/** Wizard step ids (string keys for the integrated registration flow). */
export type RegistrationStepId =
  | "quickCode"
  | "account"
  | "mailing"
  | "jurisdictions"
  | "organization"
  | "agencyInfo"
  | "agencyCapability"
  | "agencyProcedure"
  | "agencyTraining"
  | "dispatchCapabilities"
  | "agencyAgreement"
  | "additionalQuestions";

export type RegistrationStepDefinition = {
  id: RegistrationStepId;
  /** Stakeholder types that see this step (before dispatch conditionals). */
  visibleTo: readonly StakeholderType[];
};

const ALL_TYPES: readonly StakeholderType[] = [
  STAKEHOLDER_AFFECTED_PUBLIC,
  STAKEHOLDER_COMMANDER,
  STAKEHOLDER_EMERGENCY_RESPONDER,
  STAKEHOLDER_EXCAVATOR,
  STAKEHOLDER_FARMER,
  STAKEHOLDER_OTHER,
  STAKEHOLDER_PUBLIC_OFFICIAL,
  STAKEHOLDER_SCHOOL_REP,
  STAKEHOLDER_TIER_II,
];

const ER_TYPES: readonly StakeholderType[] = [
  STAKEHOLDER_COMMANDER,
  STAKEHOLDER_EMERGENCY_RESPONDER,
  STAKEHOLDER_TIER_II,
];

const COMMANDER_TIER: readonly StakeholderType[] = [STAKEHOLDER_COMMANDER, STAKEHOLDER_TIER_II];

const NON_AP_WITH_ORG: readonly StakeholderType[] = [
  STAKEHOLDER_COMMANDER,
  STAKEHOLDER_EMERGENCY_RESPONDER,
  STAKEHOLDER_EXCAVATOR,
  STAKEHOLDER_FARMER,
  STAKEHOLDER_OTHER,
  STAKEHOLDER_PUBLIC_OFFICIAL,
  STAKEHOLDER_SCHOOL_REP,
  STAKEHOLDER_TIER_II,
];

/**
 * Base wizard steps in display order.
 * Mailing precedes Jurisdictions (buxus-apps improvement over legacy buxusweb order).
 */
export const REGISTRATION_STEP_DEFINITIONS: readonly RegistrationStepDefinition[] = [
  { id: "quickCode", visibleTo: ALL_TYPES },
  { id: "account", visibleTo: ALL_TYPES },
  { id: "mailing", visibleTo: ALL_TYPES },
  {
    id: "jurisdictions",
    visibleTo: ER_TYPES,
  },
  { id: "organization", visibleTo: NON_AP_WITH_ORG },
  { id: "agencyInfo", visibleTo: ER_TYPES },
  { id: "agencyCapability", visibleTo: COMMANDER_TIER },
  { id: "agencyProcedure", visibleTo: COMMANDER_TIER },
  { id: "agencyTraining", visibleTo: COMMANDER_TIER },
  /** Inserted dynamically when dispatch triggers fire; defined here for ordering reference. */
  { id: "dispatchCapabilities", visibleTo: [] },
  { id: "agencyAgreement", visibleTo: COMMANDER_TIER },
  { id: "additionalQuestions", visibleTo: ALL_TYPES },
];

export function isErStakeholderType(stakeholderType: string): boolean {
  return ER_TYPES.includes(stakeholderType as StakeholderType);
}

export function needsOrganizationStep(stakeholderType: string): boolean {
  return NON_AP_WITH_ORG.includes(stakeholderType as StakeholderType);
}

export function needsJurisdictionsStep(stakeholderType: string): boolean {
  return isErStakeholderType(stakeholderType);
}

export function isAffectedPublic(stakeholderType: string): boolean {
  return stakeholderType === STAKEHOLDER_AFFECTED_PUBLIC;
}
